import { round } from '@/utils/mathUtils';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Database,
  DollarSign,
  FileText,
  Hash,
  Package2,
  TrendingDown,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { DataTable } from '@/UI/TableShadcn';
import { formatNumberWithCommas } from '@/utils/FormatNumber';

import type { ICompanyItemGroupedSupplyInterface } from '../../api/CompanyItems.interface';
import ItemColumn from '../itemTable/ItemColumn';

type Props = {
  items: ICompanyItemGroupedSupplyInterface[] | undefined;
};

function GroupItemsTable({ items }: Props) {
  const columns = useMemo<ColumnDef<ICompanyItemGroupedSupplyInterface>[]>(
    () => [
      {
        accessorKey: 'ID',
        header: () => (
          <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
            <Hash className="h-3.5 w-3.5" />
            <span>#</span>
          </div>
        ),
        size: 60,
        cell: (row) => {
          return (
            <div className="flex items-center justify-center">
              <Badge variant="outline" className="h-6 w-8 font-mono text-xs">
                {row.row.index + 1}
              </Badge>
            </div>
          );
        },
      },
      {
        accessorKey: 'ItemName',
        header: () => (
          <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
            <Package2 className="h-4 w-4" />
            <span>تفاصيل الصنف</span>
          </div>
        ),
        size: 280,
        cell: (row) => {
          return (
            <ItemColumn
              itemID={row?.row?.original?.ItemID}
              itemName={row?.row?.original?.ItemName}
            />
          );
        },
      },
      {
        accessorKey: 'UnitPrice',
        header: () => (
          <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
            <DollarSign className="h-4 w-4" />
            <span>سعر الجمهور</span>
          </div>
        ),
        size: 120,
        cell: (row) => {
          const price = round(row?.row?.original?.UnitPrice || 0, 2);
          return (
            <div className="flex items-center justify-center">
              <div className="rounded-lg bg-emerald-50 px-3 py-2 dark:bg-emerald-950/30">
                <span className="font-mono text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                  {price || '0'}
                </span>
                <span className="mr-1 text-xs text-emerald-600 dark:text-emerald-500">
                  ج.م
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'Stock',
        header: () => (
          <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
            <Database className="h-4 w-4" />
            <span>الرصيد</span>
          </div>
        ),
        size: 110,
        cell: (row) => {
          const stock = round(row?.row?.original?.Stock || 0, 2);
          const isLowStock = stock < 10;
          return (
            <div className="flex items-center justify-center">
              <div
                className={cn(
                  'rounded-lg px-3 py-2',
                  isLowStock
                    ? 'bg-red-50 dark:bg-red-950/30'
                    : 'bg-blue-50 dark:bg-blue-950/30',
                )}
              >
                <span
                  className={cn(
                    'font-mono text-sm font-bold',
                    isLowStock
                      ? 'text-red-700 dark:text-red-400'
                      : 'text-blue-700 dark:text-blue-400',
                  )}
                >
                  {stock || '0'}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'TotalPurchases',
        header: () => (
          <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
            <TrendingUp className="h-4 w-4" />
            <span>الوارد</span>
          </div>
        ),
        size: 110,
        cell: (row) => {
          const purchases = round(row?.row?.original?.TotalPurchases || 0, 2);
          return (
            <div className="flex items-center justify-center">
              <div className="rounded-lg bg-indigo-50 px-3 py-2 dark:bg-indigo-950/30">
                <span className="font-mono text-sm font-bold text-indigo-700 dark:text-indigo-400">
                  {purchases || '0'}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'TotalReturnsSupplier',
        header: () => (
          <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
            <TrendingDown className="h-4 w-4" />
            <span>مرتجع مورد</span>
          </div>
        ),
        size: 120,
        cell: (row) => {
          const returns = round(
            row?.row?.original?.TotalReturnsSupplier || 0,
            2,
          );
          return (
            <div className="flex items-center justify-center">
              {returns > 0 ? (
                <div className="rounded-lg bg-orange-50 px-3 py-2 dark:bg-orange-950/30">
                  <span className="font-mono text-sm font-bold text-orange-700 dark:text-orange-400">
                    {returns}
                  </span>
                </div>
              ) : (
                <span className="text-sm text-slate-400">-</span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'StockValue',
        header: () => (
          <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
            <Wallet className="h-4 w-4" />
            <span>قيمة الرصيد</span>
          </div>
        ),
        size: 140,
        cell: (row) => {
          const value = formatNumberWithCommas(
            row?.row?.original?.StockValue || 0,
          );
          return (
            <div className="flex items-center justify-center">
              <div className="rounded-lg bg-purple-50 px-3 py-2 dark:bg-purple-950/30">
                <span className="font-mono text-sm font-bold text-purple-700 dark:text-purple-400">
                  {value}
                </span>
                <span className="mr-1 text-xs text-purple-600 dark:text-purple-500">
                  ج.م
                </span>
              </div>
            </div>
          );
        },
      },
    ],
    [], // end
  );

  return (
    <div className="flex h-full w-full flex-col">
      {items && items.length > 0 ? (
        <div className="min-h-0 flex-1 p-4">
          <DataTable
            columns={columns}
            data={items}
            isPaginated={true}
            pageSize={10}
            isTopToolbar={true}
            isRowSelection={false}
            heightTable="calc(75vh - 200px)"
            visibleColumns={[]}
          />
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="max-w-md space-y-4 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
              <FileText className="h-8 w-8 text-slate-400 dark:text-slate-500" />
            </div>
            <div>
              <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
                لا توجد بيانات
              </h3>
              <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                لم يتم العثور على أي حركات للشركة المختارة خلال الفترة الزمنية
                المحددة
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
              <h4 className="mb-2 text-sm font-medium text-slate-900 dark:text-slate-100">
                جرب التالي:
              </h4>
              <ul className="space-y-1 text-right text-xs text-slate-600 dark:text-slate-400">
                <li>• تغيير الفترة الزمنية</li>
                <li>• اختيار شركة أخرى</li>
                <li>• مراجعة فلاتر البحث</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GroupItemsTable;
